import { Citation } from "src/taskpane/helpers/Models";
import { CitationSubtitle, CitationTitleContainer } from "./components";
import CollapseGroup from "./collapsables";
import { ChatInput } from "../chat/chat-input";
import { Collapsible } from "./collapsable";
import { memo, useEffect, useState } from "react";
import { useChat } from "ai/react";
import { useAuth } from "@clerk/clerk-react";
import { environment, nextJsEndpoint } from "../../utils/constants";
import { isRunningInBrowser } from "../App";
import { analytics } from "../../index";

export const chatURL = nextJsEndpoint[environment] + "/api/opinion/{{opinion_id}}/chat";

export enum Tabs {
  chat = "chat",
  citedExcerpt = "citedExcerpt",
}

export const CitationContainer = memo(
  ({ citation }: { citation: Citation }) => {
    const [activeTab, setActiveTab] = useState<Tabs | undefined>(Tabs.chat);
    const [isStreaming, setIsStreaming] = useState(false);
    const { getToken } = useAuth();
    const [token, setToken] = useState<string | null>(null);

    const handleToggle = (tab: Tabs) => {
      setActiveTab(activeTab === tab ? undefined : tab);
      analytics.track(`tab toggled`, {
        tabName: tab,
      });
    };

    const { messages, setMessages, isLoading, append, stop } = useChat({
      api: chatURL.replace("{{opinion_id}}", citation.opinion_id ?? ""),
      onResponse: () => {
        setIsStreaming(true);
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
      onError: (error) => {
        console.error("Error in chat with document:", error);
        setIsStreaming(false);
        stop();
      },
      onFinish: () => {
        setIsStreaming(false);
      },
      sendExtraMessageFields: true,
    });

    const sendMessage = async (input: string) => {
      const t = await getToken();
      setToken(t);
      setTimeout(() => {
        append({
          role: "user",
          content: input,
        });
        setActiveTab(Tabs.chat);
      }, 100);
    };

    useEffect(() => {
      analytics.track(`Citation is selected`, {
        caseName: citation.case_name,
      });
      setMessages([]);
      getToken().then((t) => {
        setToken(t);
      });
    }, [citation.opinion_id]);

    useEffect(() => {
      if (messages.length === 0 && citation.opinion_id && token && !isLoading) {
        append({
          role: "user",
          content: "What can you tell me about this case?",
        });
      }
    }, [messages, citation, token, isLoading]);

    return (
      <div className="box-border h-[calc(100vh-50px)] flex flex-col p-2 gap-1">
        <div className="overflow-x-hidden whitespace-nowrap text-ellipsis">
          <CitationTitleContainer caseName={citation.case_name} link={citation.hyperlink} />
          <div className="flex flex-row items-center flex-shrink-0 gap-[8px] h-[18px]">
            <CitationSubtitle citation={citation} />
          </div>
        </div>
        <div className="flex flex-row gap-1 py-[5px] h-[40px]">
          <Collapsible
            title={isRunningInBrowser() ? "Outcome" : "Chat"}
            isExpanded={activeTab === Tabs.chat}
            onClick={() => handleToggle(Tabs.chat)}
          />
          <Collapsible
            title="Show Case"
            isExpanded={activeTab === Tabs.citedExcerpt}
            onClick={() => handleToggle(Tabs.citedExcerpt)}
          />
        </div>
        <CollapseGroup
          citation={citation}
          messages={messages}
          isLoading={isLoading}
          activeTab={activeTab}
          isStreaming={isStreaming}
        />
        {!isRunningInBrowser() && (
          <ChatInput
            onSubmit={(input) => {
              sendMessage(input);
              analytics.track(`chat message sent`);
            }}
            isLoading={isLoading}
            isDisabled={isLoading}
          />
        )}
      </div>
    );
  },
  (prevProps, nextProps) => prevProps.citation.opinion_id === nextProps.citation.opinion_id
);
